// framework
import { immerable, produce } from "immer";
// api
import * as Client from "../../../../api/Client";
import { CallResultError } from "../../../../api/callHelper";

export interface IReferenceDataViewModel {
    readonly honorifics: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseHonorificDto[];
    readonly countrySubdivisions: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseCountrySubDivisionDto[];
    readonly countries: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseCountryDto[];
    readonly boreholes: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseBoreholeDto[];
    readonly sampleTypes: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseSampleTypeDto[];
}

export interface ISampleViewModel {
    readonly uniqueId: string;
    readonly borehole?: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseBoreholeDto | undefined;
    readonly intervalTop?: number | undefined;
    readonly intervalBottom?: number | undefined;
    readonly sampleTypes?: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseSampleTypeDto[] | undefined;
}

export interface ICoreCuttingsRequestViewModel {
    readonly honorific?: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseHonorificDto | undefined;
    readonly firstName?: string | undefined;
    readonly lastName?: string | undefined;
    readonly companyName?: string | undefined;
    readonly contactNumber?: string | undefined;
    readonly emailAddress?: string | undefined;
    readonly streetAddress?: string | undefined;
    readonly suburb?: string | undefined;
    readonly countrySubdivision?: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseCountrySubDivisionDto | undefined;
    readonly country?: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseCountryDto | undefined;
    readonly postcode?: string | undefined;
    readonly isAcademia?: boolean | undefined;
    readonly supervisorHonorific?: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseHonorificDto | undefined;
    readonly supervisorFirstName?: string | undefined;
    readonly supervisorLastName?: string | undefined;
    readonly supervisorUniversityName?: string | undefined;
    readonly supervisorContactNumber?: string | undefined;
    readonly supervisorEmailAddress?: string | undefined;
    readonly supervisorSchoolName?: string | undefined;
    readonly studyDescription?: string | undefined;
    readonly samples: ISampleViewModel[];
}

export interface IRootViewModel {
    readonly userToken: string;
    readonly statusMessages?: Client.StatusMessagesDto;
    readonly isSubmitted: boolean;
    readonly isDirty: boolean;
    readonly isBusy: boolean;
    readonly error: CallResultError | undefined;
    readonly coreCuttingsRequest: ICoreCuttingsRequestViewModel;
    readonly referenceData: IReferenceDataViewModel;

    coreCuttingsRequestDetailsChanged(values: ICoreCuttingsRequestViewModel): IRootViewModel;
    coreCuttingsRequestDetailsReset(): IRootViewModel;
    sampleAdded(value: ISampleViewModel): IRootViewModel;
    sampleRemoved(value: ISampleViewModel): IRootViewModel;
    referenceDataLoading(): IRootViewModel;
    referenceDataLoaded(response: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseDto | undefined, error: CallResultError | undefined): IRootViewModel;
    coreCuttingsRequestCreating(): IRootViewModel;
    coreCuttingsRequestCreated(response: Client.CreatePublicCoreCuttingsRequestResponseDto | undefined, error: CallResultError | undefined): IRootViewModel;
}

export class RootViewModel implements IRootViewModel {
    [immerable] = true;

    constructor(userToken: string) {
        this.userToken = userToken;
        this.statusMessages = undefined;
        this.isSubmitted = false;
        this.isDirty = false;
        this.isBusy = false;
        this.error = undefined;
        this.coreCuttingsRequest = {
            samples: [],
        };
        this.referenceData = {
            honorifics: [],
            countrySubdivisions: [],
            countries: [],
            boreholes: [],
            sampleTypes: [],
        };
    }

    public readonly userToken: string;
    public readonly statusMessages?: Client.StatusMessagesDto;
    public readonly isSubmitted: boolean;
    public readonly isDirty: boolean;
    public readonly isBusy: boolean;
    public readonly error: CallResultError | undefined;
    public readonly coreCuttingsRequest: ICoreCuttingsRequestViewModel;
    public readonly referenceData: IReferenceDataViewModel;

    public coreCuttingsRequestDetailsChanged(values: ICoreCuttingsRequestViewModel): IRootViewModel {
        return produce(this, (draft) => {
            draft.coreCuttingsRequest.honorific = values.honorific;
            draft.coreCuttingsRequest.firstName = values.firstName;
            draft.coreCuttingsRequest.lastName = values.lastName;
            draft.coreCuttingsRequest.companyName = values.companyName;
            draft.coreCuttingsRequest.contactNumber = values.contactNumber;
            draft.coreCuttingsRequest.emailAddress = values.emailAddress;
            draft.coreCuttingsRequest.streetAddress = values.streetAddress;
            draft.coreCuttingsRequest.suburb = values.suburb;
            draft.coreCuttingsRequest.postcode = values.postcode;
            draft.coreCuttingsRequest.countrySubdivision = values.countrySubdivision;
            draft.coreCuttingsRequest.country = values.country;
            draft.coreCuttingsRequest.isAcademia = values.isAcademia;
            draft.coreCuttingsRequest.supervisorHonorific = values.supervisorHonorific;
            draft.coreCuttingsRequest.supervisorFirstName = values.supervisorFirstName;
            draft.coreCuttingsRequest.supervisorLastName = values.supervisorLastName;
            draft.coreCuttingsRequest.supervisorUniversityName = values.supervisorUniversityName;
            draft.coreCuttingsRequest.supervisorContactNumber = values.supervisorContactNumber;
            draft.coreCuttingsRequest.supervisorEmailAddress = values.supervisorEmailAddress;
            draft.coreCuttingsRequest.supervisorSchoolName = values.supervisorSchoolName;
            draft.coreCuttingsRequest.studyDescription = values.studyDescription;
            draft.isDirty = true;
        });
    }

    public coreCuttingsRequestDetailsReset(): IRootViewModel {
        return produce(this, (draft) => {
            draft.coreCuttingsRequest = {
                samples: [],
                country: draft.referenceData.countries.find((c) => c.name === "Australia"),
            };
            draft.statusMessages = undefined;
            draft.isSubmitted = false;
            draft.isDirty = false;
            draft.error = undefined;
        });
    }

    public sampleAdded(value: ISampleViewModel) {
        return produce(this, (draft) => {
            draft.coreCuttingsRequest.samples.push(value);
            draft.coreCuttingsRequest.samples.sort((a: ISampleViewModel, b: ISampleViewModel) => a.borehole!.name.localeCompare(b.borehole!.name));
            draft.isDirty = true;
        });
    }

    public sampleRemoved(value: ISampleViewModel) {
        return produce(this, (draft) => {
            const index = draft.coreCuttingsRequest.samples.findIndex((s) => s.uniqueId === value.uniqueId);
            if (index !== -1) draft.coreCuttingsRequest.samples.splice(index, 1);
            draft.isDirty = true;
        });
    }

    public referenceDataLoading(): IRootViewModel {
        return produce(this, (draft) => {
            draft.isBusy = true;
        });
    }

    public referenceDataLoaded(response: Client.GetCreatePublicCoreCuttingsRequestReferenceDataResponseDto | undefined, error: CallResultError | undefined): IRootViewModel {
        return produce(this, (draft) => {
            if (response) {
                draft.referenceData.honorifics = response.honorifics;
                draft.referenceData.countrySubdivisions = response.countrySubDivisions;
                draft.referenceData.countries = response.countries;
                draft.referenceData.boreholes = response.boreholes;
                draft.referenceData.sampleTypes = response.sampleTypes;
                draft.coreCuttingsRequest.country = response.countries.find((c) => c.name === "Australia");
            }
            draft.isBusy = false;
            draft.error = error;
        });
    }

    public coreCuttingsRequestCreating(): IRootViewModel {
        return produce(this, (draft) => {
            draft.isBusy = true;
        });
    }

    public coreCuttingsRequestCreated(response: Client.CreatePublicCoreCuttingsRequestResponseDto | undefined, error: CallResultError | undefined): IRootViewModel {
        return produce(this, (draft) => {
            if (response) {
                draft.statusMessages = response.statusMessages;
                if (response.statusMessages.isSuccess) {
                    draft.isSubmitted = true;
                    draft.isDirty = false;
                }
            }
            draft.isBusy = false;
            draft.error = error;
        });
    }
}
